import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import CancelRoundedIcon from '@material-ui/icons/CancelRounded';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

const useStyles = makeStyles((theme) => ({
  addPlusIcon: {
    color: theme.palette.primary.main,
  },
  removePlusIcon: {
    color: theme.palette.error.main,
  },
  addPlusIconDisabled: {
    color: 'gray',
  },
  root: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  spotRow: {
    display: 'flex',
    alignItems: 'center',
  },
  spotLabel: {
    marginRight: theme.spacing(2),
  },
  // View mode — same muted soft box as Issue date / contact fields
  spotValueBox: {
    minWidth: 140,
    height: 36,
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #F7F8FA',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: '#F7F8FC',
  },
  spotValue: {
    fontWeight: 400,
    color: '#9AA0A6',
    lineHeight: 1,
  },
  // EDIT unlocked — editable TextField with darker outline
  spotFieldEditing: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#F7F8FC',
    },
    '& .MuiOutlinedInput-input': {
      backgroundColor: '#F7F8FC',
    },
    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
      borderColor: '#D0D5DD !important',
      borderWidth: '1px !important',
      boxShadow: 'none !important',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#D0D5DD !important',
      borderWidth: '1px !important',
      boxShadow: 'none !important',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#D0D5DD !important',
      borderWidth: '1px !important',
      boxShadow: 'none !important',
    },
  },
}));

const TenantReservedSpots = ({
  register,
  fieldErrors,
  reservedSpots = [],
  isEditMode,
  getValues,
  setValue,
  setReservedSpots,
}) => {
  const spots = Array.isArray(reservedSpots) ? reservedSpots : [];
  const [reservedSpotsCount, setReservedSpotsCount] = useState(['ok']);
  const rules = useSelector((state) => state.rules);
  const classes = useStyles();
  const { allow_tenant_to_own_reserved_spots } = rules;

  const handleAddSpot = () => {
    setReservedSpotsCount([...reservedSpotsCount, 'ok']);
  };

  const handleRemoveSpot = (index) => {
    const count = reservedSpotsCount.filter((_, i) => i !== index);
    const data = getValues();
    const currentSpots = Array.isArray(data.reserved_spots)
      ? data.reserved_spots
      : [];

    if (currentSpots.length === 0 || count.length === 0) {
      // Last row: clear form + parent list so Save sends no kept ids (delete).
      setValue('reserved_spots', [{ id: '', spot: '' }]);
      setValue('reserved_spots[0].id', '');
      setValue('reserved_spots[0].spot', '');
      setReservedSpotsCount(['ok']);
      if (typeof setReservedSpots === 'function') {
        setReservedSpots([]);
      }
      return;
    }

    const nextSpots =
      index === 0
        ? [...currentSpots.slice(1)]
        : [
            ...currentSpots.slice(0, index),
            ...currentSpots.slice(index + 1),
          ].filter((value) => value && value.spot);

    setReservedSpotsCount(count);
    setValue('reserved_spots', nextSpots);
    if (typeof setReservedSpots === 'function') {
      setReservedSpots(nextSpots.filter((value) => value && value.id));
    }
  };

  useEffect(() => {
    setReservedSpotsCount(
      spots.length > 0 ? spots.map(() => 'ok') : ['ok']
    );
  }, [allow_tenant_to_own_reserved_spots, reservedSpots]);

  if (!allow_tenant_to_own_reserved_spots) {
    return null;
  }

  return (
    <Box className={classes.root}>
      {reservedSpotsCount.map((item, index) => {
        const spotValue = spots[index] ? spots[index].spot : '';
        const spotId = spots[index] ? spots[index].id : '';

        return (
          <Box key={index} className={classes.spotRow}>
            <Typography
              variant="subtitle2"
              color="primary"
              className={classes.spotLabel}
            >
              Reserved Spot:
            </Typography>
            <input
              type="hidden"
              name={`reserved_spots[${index}].id`}
              defaultValue={spotId}
              ref={register}
              key={`reserved_spots-id-${index}-${spotId}`}
            />
            {isEditMode ? (
              <TextField
                className={classes.spotFieldEditing}
                name={`reserved_spots[${index}].spot`}
                size="small"
                defaultValue={spotValue}
                inputRef={register}
                error={!!fieldErrors.reserved_spots?.[index]}
                InputProps={{
                  endAdornment: (
                    <InputAdornment>
                      <IconButton
                        aria-label="Remove reserved spot"
                        onClick={() => handleRemoveSpot(index)}
                        edge="end"
                      >
                        <CancelRoundedIcon
                          fontSize="small"
                          className={classes.removePlusIcon}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                autoComplete="off"
                helperText={
                  fieldErrors.reserved_spots?.[index]
                    ? fieldErrors.reserved_spots?.[index]
                    : null
                }
                style={{ maxWidth: 180, minWidth: 120 }}
              />
            ) : (
              <>
                <Box className={classes.spotValueBox}>
                  <Typography variant="body1" className={classes.spotValue}>
                    {spotValue}
                  </Typography>
                </Box>
                <input
                  type="hidden"
                  name={`reserved_spots[${index}].spot`}
                  defaultValue={spotValue}
                  ref={register}
                  key={`reserved-spots-spot-${index}-${spotValue}`}
                />
              </>
            )}
            {index === reservedSpotsCount.length - 1 && (
              <Button
                type="button"
                disabled={!isEditMode}
                onClick={handleAddSpot}
              >
                <AddCircleIcon
                  className={
                    isEditMode
                      ? classes.addPlusIcon
                      : classes.addPlusIconDisabled
                  }
                />
              </Button>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default TenantReservedSpots;
